import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import SceneShell from "../components/SceneShell";
import CountUp from "../components/CountUp";

/**
 * Scene 27 — Where we're trying to go · 50,000 videos comparison.
 * Side-by-side: current model £250k vs new system £7,500.
 */

export default function Scene27_FiftyKVideos() {
  return (
    <SceneShell variant="forest" align="center" data-testid="scene-fifty-k-videos">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(150,193,31,0.16) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mb-5 text-center mx-auto flex items-center justify-center gap-2"
        >
          <TrendingUp className="w-3.5 h-3.5" />
          Where we're going · scale-up
        </motion.div>

        {/* The 50,000 hero number */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7, filter: "blur(20px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.4, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mt-10 mb-2"
        >
          <div
            className="kn-display tnum leading-[0.85]"
            style={{
              fontSize: "clamp(80px, 14vw, 200px)",
              background: "linear-gradient(135deg, #00deb6 0%, #96C11F 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              textShadow: "0 0 80px rgba(0,222,182,0.35)",
            }}
            data-testid="fifty-k-hero"
          >
            <CountUp
              from={0}
              to={50000}
              duration={2.2}
              startDelay={1.0}
            />
          </div>
          <div
            className="mt-1 text-base sm:text-lg tracking-[0.18em] uppercase"
            style={{ color: "rgba(255,255,255,0.7)" }}
          >
            videos · per year
          </div>
        </motion.div>

        {/* Side-by-side comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
          {/* CURRENT MODEL */}
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.0, delay: 2.4, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl p-7 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, rgba(60,12,22,0.92) 0%, rgba(90,20,32,0.85) 100%)",
              border: "1px solid rgba(220,80,100,0.55)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.04), 0 20px 50px -20px rgba(0,0,0,0.6), 0 0 40px rgba(200,58,79,0.18)",
            }}
            data-testid="fifty-k-current"
          >
            <div
              className="text-[11px] tracking-[0.32em] uppercase mb-4"
              style={{ color: "#ff8597" }}
            >
              Current model
            </div>
            <div className="flex items-center gap-3 text-sm sm:text-base text-white/85 mb-5 tnum">
              <span>50,000</span>
              <span className="text-white/40">×</span>
              <span style={{ color: "#ff8597" }}>£5</span>
              <span className="text-white/40">=</span>
            </div>
            <div
              className="kn-display tnum text-[58px] sm:text-[72px] lg:text-[88px] leading-none whitespace-nowrap"
              style={{
                color: "#ff8597",
                fontWeight: 800,
                textShadow: "0 0 24px rgba(255,90,114,0.35)",
              }}
            >
              <CountUp
                from={0}
                to={250000}
                prefix="£"
                duration={2.4}
                startDelay={2.7}
              />
            </div>
            <div
              className="mt-3 text-sm"
              style={{ color: "rgba(255,255,255,0.7)" }}
            >
              per year · in editing alone
            </div>
          </motion.div>

          {/* NEW SYSTEM */}
          <motion.div
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.0, delay: 2.7, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl p-7 relative overflow-hidden glass"
            style={{
              boxShadow: "0 0 60px rgba(150,193,31,0.20)",
            }}
            data-testid="fifty-k-new"
          >
            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{ opacity: [0.5, 0.85, 0.5] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse at 80% 20%, rgba(150,193,31,0.22) 0%, transparent 60%)",
              }}
            />
            <div className="relative">
              <div
                className="text-[11px] tracking-[0.32em] uppercase mb-4"
                style={{ color: "#96C11F" }}
              >
                Your system
              </div>
              <div className="flex items-center gap-3 text-sm sm:text-base text-white/85 mb-5 tnum">
                <span>50,000</span>
                <span className="text-white/30">×</span>
                <span style={{ color: "#96C11F" }}>£0.15</span>
                <span className="text-white/30">=</span>
              </div>
              <div
                className="kn-display tnum text-[58px] sm:text-[72px] lg:text-[88px] leading-none whitespace-nowrap"
                style={{
                  background:
                    "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  fontWeight: 900,
                }}
              >
                <CountUp
                  from={0}
                  to={7500}
                  prefix="£"
                  duration={2.4}
                  startDelay={3.0}
                />
              </div>
              <div
                className="mt-3 text-sm"
                style={{ color: "rgba(255,255,255,0.55)" }}
              >
                per year · all in
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </SceneShell>
  );
}
